// URL-state hook. Implements the F-004 cursor-reset rule from
// frontend-design §1 / §3 Pagination contract:
//
//   "Any change to filters or sort drops the cursor (replaces the URL
//   with an absent `cursor` param) before fetching… Only Show-more /
//   Next-page actions advance the cursor while preserving filters and
//   sort verbatim."
//
// Two separate setters:
//   - setFilterOrSort(...) → strips `cursor` from the URL
//   - setCursor(value)     → preserves filters / sort verbatim

import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

export interface UrlStateApi {
  // Get a single-valued param.
  get: (key: string) => string | null;
  // Get a multi-valued param (e.g., ?status=draft&status=active).
  getAll: (key: string) => string[];
  // Update one or more filter/sort params, dropping `cursor` in the
  // same transition. Pass null to remove a param.
  setFilterOrSort: (
    updates: Record<string, string | string[] | null | undefined>,
  ) => void;
  // Advance only the cursor, preserving every other param verbatim.
  setCursor: (cursor: string | null) => void;
  // Drop everything (used by "Reset filters" / "Clear filters").
  clearAll: () => void;
}

export function useUrlState(): UrlStateApi {
  const [params, setParams] = useSearchParams();

  const get = useCallback(
    (key: string) => params.get(key),
    [params],
  );
  const getAll = useCallback(
    (key: string) => params.getAll(key),
    [params],
  );

  const setFilterOrSort = useCallback(
    (updates: Record<string, string | string[] | null | undefined>) => {
      const next = new URLSearchParams(params);
      // Always strip cursor on a filter / sort change.
      next.delete('cursor');
      for (const [key, value] of Object.entries(updates)) {
        next.delete(key);
        if (value === null || value === undefined) continue;
        if (Array.isArray(value)) {
          for (const v of value) next.append(key, v);
        } else {
          next.set(key, value);
        }
      }
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const setCursor = useCallback(
    (cursor: string | null) => {
      const next = new URLSearchParams(params);
      if (cursor === null) next.delete('cursor');
      else next.set('cursor', cursor);
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const clearAll = useCallback(() => {
    setParams(new URLSearchParams(), { replace: false });
  }, [setParams]);

  return { get, getAll, setFilterOrSort, setCursor, clearAll };
}
